<template>
  <section id="faq">
    <div class="container">
      <h1 class="sectionTitle">Frequently Asked Questions</h1>

      <div class="faq-list">
        <div
          v-for="(item, index) in faqItems"
          :key="index"
          class="faq-item"
          :class="{ 'is-open': openIndex === index }"
        >
          <button
            class="faq-question"
            @click="toggleFAQ(index)"
            :aria-expanded="(openIndex === index).toString()"
            :aria-controls="'faq-answer-' + index"
          >
            <span>{{ item.question }}</span>
            <svg
              class="faq-icon"
              :class="{ 'is-rotated': openIndex === index }"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div
            :id="'faq-answer-' + index"
            class="faq-answer"
            :style="{ maxHeight: openIndex === index ? answerHeights[index] + 'px' : '0' }"
          >
            <div class="faq-answer-content" :ref="'answer-' + index">
              <p v-html="item.answer"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "FAQ",
  data() {
    return {
      openIndex: null,
      answerHeights: [],
      faqItems: [
        {
          question: "What is Stardust Cookie Cutter?",
          answer: "Stardust Cookie Cutter is a free, open-source browser extension that automatically handles cookie consent pop-ups based on your preferences. Install it once, set your preferences, and never see cookie banners again."
        },
        {
          question: "Is Cookie Cutter really free?",
          answer: "Yes! Cookie Cutter is completely free to use. We believe in user privacy and transparency. The project is funded by donations from users who find value in our tools."
        },
        {
          question: "Which browsers are supported?",
          answer: "Cookie Cutter is available for Chrome, Firefox, Safari, Edge, Brave, and Opera. We support both desktop and mobile versions where applicable."
        },
        {
          question: "Does Cookie Cutter collect my data?",
          answer: "No. Cookie Cutter does not collect, store, or transmit any personal data. Your preferences are stored locally on your device. You can verify this by checking our <a href='https://github.com/stardustnetwork' target='_blank' rel='noopener noreferrer'>open-source code on GitHub</a>."
        },
        {
          question: "How can I support this project?",
          answer: "We appreciate your support! You can <a href='/contribute#donate'>donate</a> to help us continue development, contribute code on <a href='https://github.com/stardustnetwork' target='_blank' rel='noopener noreferrer'>GitHub</a>, or spread the word to friends and colleagues."
        },
        {
          question: "Can I customize my cookie preferences?",
          answer: "Absolutely! Cookie Cutter allows you to set granular preferences for different types of cookies (necessary, functional, analytics, advertising). You can also whitelist or blacklist specific websites."
        },
        {
          question: "Is Cookie Cutter GDPR compliant?",
          answer: "Yes. Cookie Cutter helps you enforce your GDPR rights by automatically rejecting non-essential cookies unless you explicitly consent. This puts you in full control of your data."
        },
        {
          question: "Where can I report issues or request features?",
          answer: "You can report issues on our <a href='https://github.com/stardustnetwork' target='_blank' rel='noopener noreferrer'>GitHub repository</a>, join discussions on our <a href='https://www.reddit.com/r/StardustForum/' target='_blank' rel='noopener noreferrer'>Reddit forum</a>, or chat with the community on <a href='https://discord.gg/Ssh9faHbSu' target='_blank' rel='noopener noreferrer'>Discord</a>."
        }
      ]
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateHeights();
    });
  },
  methods: {
    toggleFAQ(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
    calculateHeights() {
      this.answerHeights = this.faqItems.map((item, index) => {
        const ref = this.$refs['answer-' + index];
        return ref && ref[0] ? ref[0].scrollHeight : 0;
      });
    }
  }
};
</script>

<style scoped>
section {
  background-color: var(--color-charcoal-mist-30);
  backdrop-filter: var(--blur-glassmorphism);
  filter: var(--shadow-card);
  padding: var(--spacing-3xl) var(--container-padding);
  margin-bottom: 0;
  position: relative;
  z-index: 1;
}

.container {
  max-width: 900px;
  margin: 0 auto;
}

h1.sectionTitle {
  text-align: center;
  margin-bottom: var(--spacing-2xl);
  color: var(--color-stardust-gold);
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.faq-item {
  background-color: var(--color-charcoal-mist-70);
  border-radius: var(--radius-medium-lg);
  overflow: hidden;
  transition: var(--transition-standard);
  border: 2px solid transparent;
}

.faq-item:hover {
  border-color: var(--color-gold-glow-30);
  background-color: var(--color-charcoal-mist-85);
}

.faq-item.is-open {
  border-color: var(--color-gold-glow-50);
}

.faq-question {
  width: 100%;
  padding: var(--spacing-lg) var(--spacing-lg);
  background: none;
  border: none;
  color: var(--color-pure-white);
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  transition: var(--transition-standard);
  font-family: var(--font-primary);
}

.faq-question:hover {
  color: var(--color-stardust-gold);
}

.faq-question:focus-visible {
  outline: 2px solid var(--color-stardust-gold);
  outline-offset: -2px;
}

.faq-icon {
  flex-shrink: 0;
  color: var(--color-stardust-gold);
  transition: var(--transition-standard);
}

.faq-icon.is-rotated {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
}

.faq-answer-content {
  padding: 0 var(--spacing-lg) var(--spacing-lg) var(--spacing-lg);
}

.faq-answer-content p {
  margin: 0;
  color: var(--color-pure-white);
  line-height: 1.7;
  font-size: 16px;
  opacity: 0.95;
}

.faq-answer-content a {
  color: var(--color-stardust-gold);
  text-decoration: underline;
  transition: var(--transition-quick);
}

.faq-answer-content a:hover {
  color: var(--color-stardust-gold-light);
}

.faq-answer-content a:focus-visible {
  outline: 2px solid var(--color-stardust-gold);
  outline-offset: 2px;
  border-radius: 2px;
}

@media (max-width: 768px) {
  section {
    padding: var(--spacing-2xl) var(--container-padding);
  }

  .faq-question {
    padding: var(--spacing-md) var(--container-padding);
    font-size: 16px;
  }

  .faq-answer-content {
    padding: 0 var(--container-padding) var(--spacing-md) var(--container-padding);
  }

  .faq-answer-content p {
    font-size: 14px;
  }
}

@media (max-width: 500px) {
  section {
    padding: 30px var(--spacing-md);
  }
}
</style>
